import { Checkbox, Flex, Text, Tooltip } from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { OfferPackage, PackageItemModel, PackageItemPropertyModel, PartPackage } from 'BrokerTsLib/src/dtos'
import { PackageItemLimitType, Risk } from 'BrokerTsLib/src/enums'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useContext } from 'react'

import _ from 'lodash'
import { LoadingOverlayContext } from '../../../../components/LoadingOverlay'
import type { IOffersContext } from '../../../../context/OffersProvider'
import { useGetOffersContext } from '../../../../context/useGetOffersContext'
import { useGetRiskVariants } from '../../../../hooks'
import { useResolveChangedPart } from '../../../../hooks/useOffer'
import { CheckCircle, DismissCircle, Info } from '../../../../icons'
import { useInsuranceParamsContext } from '../../../../layouts/Wizard'
import { EditablePriceCustom } from '../../../EditablePriceCustom'
import { EditablePackageItemTinySelect } from '../../../EditableTinySelect'
import type { IBodyCell } from '../columnSections'
import { OfferCellWrapper } from './OfferCellWrapper'

export function IsEditableLimit(packageItem: PackageItemModel) {
  return packageItem.limitType == PackageItemLimitType.Limit &&
    (packageItem.addable || packageItem.included) &&
    packageItem.variants &&
    (packageItem.variants.length > 1 ||
      packageItem.variants.length == 1 &&
      packageItem.variants[0].limitEditable &&
      packageItem.variants[0].minLimit != packageItem.variants[0].maxLimit)
}


export function GetPackagePartRenderType(packageItem: PackageItemModel) {
  let childrenType: 'bool' | 'limit' | 'options' | 'text' | undefined
  const isEditableLimit = IsEditableLimit(packageItem)

  switch (packageItem.limitType) {
    case PackageItemLimitType.Boolean:
      childrenType = 'bool'
      break

    case PackageItemLimitType.Limit:
      childrenType = isEditableLimit
        ? (packageItem.variants.length > 1 ? 'options' : 'limit')
        : 'text'
      break
  }

  if (!packageItem.included && !packageItem.addable) {
    childrenType = 'bool' // specialita
  }

  return childrenType
}

export const GetPackageItemLimit = (offerPart: PartPackage, packageItem: PackageItemModel | PackageItemPropertyModel) => {
  if (offerPart?.limit || offerPart?.limit == 0) {
    let limitText = undefined
    if (offerPart?.packageItemVariantId) {
      limitText = packageItem.variants.find(x => x.id == offerPart.packageItemVariantId)?.limitText
    }
    return [offerPart?.limit, offerPart?.packageItemVariantId, limitText]
  }

  if (!packageItem.variants || packageItem.variants.length == 0)
    return []

  const variant = packageItem.variants.find(x => x.isDefault) ?? packageItem.variants[0]
  return [variant.defaultLimit!, variant.id, variant.limitText]
}

export const changePartLimit = (part: PartPackage, included: boolean, limit?: number, variantId?: number) => {
  if (part.included != included && !part.packageItem.addable) // zahrnuji/odebiram riziko, krete nelze pridat/odebrat
    return false

  if (part.included == included && part.limit == limit && part.packageItemVariantId == variantId)
    return false

  if ((included === undefined || included == false) && part.included == included && limit && part.limit != limit) {
    part.included = true // pokud uživatel změní hodnotu limitu a riziko není ješte v nabídce, má se automaticky přidat - CHALOUPKA-509
  }
  else {
    part.included = included
  }

  if (part.packageItem.limitType == PackageItemLimitType.Limit) {
    part.limit = limit
    part.packageItemVariantId = variantId
  }

  part.changed = true

  return true
}

export const OfferCell = ({
  id,
  offerPart,
  packageItem,
  singleCell = true,
}
  : IBodyCell
) => {
  const part = offerPart as PartPackage

  const { updateOffer, offers } = useGetOffersContext() as IOffersContext<OfferPackage>
  const { insuranceParams } = useInsuranceParamsContext()
  const isContracted = !!insuranceParams?.generalParams.contractNumber

  const { setLoading } = useContext(LoadingOverlayContext)
  const { mutateAsync: resolvePart } = useResolveChangedPart()
  const { data: dataPropertyType } = useGetRiskVariants(Risk.PROPERTY_TYPE)

  const propertyTypes = dataPropertyType ?? []

  const o = offers.find(x => x.offerId == part.offerId)!

  const [value, packageItemVariantId, valueAsText] = GetPackageItemLimit(part, packageItem)

  const pipm = packageItem as PackageItemPropertyModel
  const tooltipValue = !singleCell && pipm?.productTypeRiskVariantId
    ? (
      <Flex flexDirection='column' gap={4}>
        <Text fontWeight='bold'>{propertyTypes.find(x => x.id == pipm.productTypeRiskVariantId)?.name}</Text>
        <Text>{packageItem.description}</Text>
      </Flex>
    )
    : packageItem.description

  const valueText = valueAsText ? valueAsText as string :
    value == 999999999 ? 'Neomezeně' :
      !!value || value == 0 ? priceFormat(value, null, '0 Kč') : undefined

  const included = !!part?.included

  const changeLimit = (included: boolean, limit?: number, variantId?: number) => {
    const offer = _.cloneDeep(o)

    // pokud je partId == 0, tak se jedna o novy part, ktery jeste neni v DB, proto hledame podle packageItemId
    const clonedPart = offer.parts.find(op => op.partId == part.partId && op.partId != 0 ||
      op.partId == 0 && op.packageItem.packageItemId && op.packageItem.packageItemId == part.packageItem.packageItemId
    ) ?? _.cloneDeep(part)

    changePartLimit(clonedPart, included, limit, variantId)
    if (!clonedPart.changed)
      return

    setLoading(true)

    resolvePart({ offer: offer, packageItemId: packageItem.packageItemId! })
      .then(({ data: updatedOffer }) => {
        updateOffer(updatedOffer) // useEffect na changed zajisti zavolani saveOfferParts a nasledne rekalkulaci
      })
      .finally(() =>
        setTimeout(() => setLoading(false), 100)) // aby loading zmizel az po renderu 
  }

  let children
  const childrenType = GetPackagePartRenderType(packageItem)

  switch (childrenType) {
    case 'bool':
      if (included) {
        children = <CheckCircle color="green.500" />
      } else {
        children = <DismissCircle color="red.500" />
      }
      break
    case 'limit': {
      const variant = packageItem.variants[0]
      children = (
        <EditablePriceCustom
          value={value}
          text={valueText}
          isDisabled={isContracted}
          min={variant.minLimit}
          max={variant.maxLimit}
          onSubmit={(limit) => {
            changeLimit(part.included, limit, variant.id)
          }}
        />
      )
      break
    }
    case 'options':
      children = <EditablePackageItemTinySelect
        isDisabled={isContracted}
        limit={part.limit}
        packageItem={packageItem}
        valueText={valueText}
        packageItemVariantId={packageItemVariantId as number}
        // showControls={included}
        // isEditing={isEditing}
        onSubmit={(limit, variantId) => changeLimit(part.included, limit, variantId)} />
      break
    case 'text':
    default:
      children = valueText
        ? <Text sx={{ p: 0, fontSize: 'sm', textDecoration: 'underline', textDecorationStyle: 'dotted', mr: isContracted ? 0 : 5 }}>
          {valueText}
        </Text>
        : <DismissCircle color="red.500" />
      break
  }

  return (
    <OfferCellWrapper key={id} sx={{ justifyContent: 'end', justifyItems: 'end' }}>
      {offerPart.limitWarning &&
        <CustomTooltip label={offerPart.limitWarning} whiteSpace='pre-wrap' maxW='xs' placement="auto" hasArrow>
          <Flex alignItems='center'>
            <Info width={4} height={4} mt={.5} transform="rotate(180deg)" />
          </Flex>
        </CustomTooltip>
      }

      <Tooltip label={tooltipValue} hasArrow placement="auto">
        <Flex sx={{ opacity: included ? 1 : 0.5, minH: '20px', whiteSpace: 'nowrap' }}>
          {children}
        </Flex>
      </Tooltip>

      <Checkbox
        size="sm"
        isDisabled={!packageItem.addable || isContracted}
        isChecked={included}
        onChange={e => changeLimit(e.target.checked, value as number, packageItemVariantId as number)}
      />

    </OfferCellWrapper>
  )
}


